'use client';

import {
  disable2FA,
  enable2FA,
  forgotPassword,
  getBackupCodes,
  loginUser,
  loginUserByPhone,
  logoutAll,
  registerUser,
  resendVerification,
  resetPassword,
  verify2FA,
  verifyEmail,
  verifyPhone,
} from '@/lib/api/auth';
import type { AuthResponse } from '@/lib/types/auth.types';
import { useState } from 'react';

interface LoginCredentials {
  email?: string;
  phone?: string;
  password: string;
}

interface RegisterData {
  fullName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  companyName?: string;
  commercialReg?: string;
  userType: 'client' | 'supplier' | 'admin';
  verifiedVia?: 'email' | 'phone';
}

export function useLogin() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      let response: AuthResponse;

      // Decide login method based on provided credentials
      if (credentials.phone && !credentials.email) {
        // Login with phone + password
        response = await loginUserByPhone({
          phone: credentials.phone,
          password: credentials.password,
        });
      } else {
        // Default to email + password
        response = await loginUser({
          email: credentials.email || '',
          password: credentials.password,
        });
      }
      setError(null); // Clear error on success
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      // Ensure error has a message
      if (!error.message || error.message.trim() === '') {
        (error as any).code = 'LOGIN_FAILED';
        error.message = 'LOGIN_FAILED';
      }
      console.log('🔴 useLogin: Setting error state:', {
        message: error.message,
        error: error,
        errorType: error.constructor.name,
      });
      setError(error);
      console.log('🔴 useLogin: Error state set, will throw error');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return { login, isLoading, error, clearError };
}

export function useRegister() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const register = async (data: RegisterData): Promise<AuthResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await registerUser({
        name: data.fullName,
        email: data.email,
        password: data.password,
        userType: data.userType,
        phone: data.phoneNumber,
        companyName: data.companyName,
        commercialRegister: data.commercialReg,
        verifiedVia: data.verifiedVia,
      });
      return response;
    } catch (err: any) {
      // Extract error message from axios error response
      let errorMessage = 'REGISTER_FAILED';
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      // Map backend-localized messages to stable codes so UI can show the correct language
      const registerErrorCodeMap: Record<string, string> = {
        'البريد الإلكتروني مستخدم مسبقًا': 'REGISTER_EMAIL_EXISTS',
        'رقم الجوال مستخدم مسبقًا': 'REGISTER_PHONE_EXISTS',
        'Email already in use': 'REGISTER_EMAIL_EXISTS',
        'Phone number already in use': 'REGISTER_PHONE_EXISTS',
      };
      const mappedCode = registerErrorCodeMap[errorMessage];
      if (mappedCode) {
        errorMessage = mappedCode;
      }
      const error = new Error(errorMessage);
      (error as any).code = mappedCode ?? 'REGISTER_FAILED';
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { register, isLoading, error };
}

export function useForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const sendResetLink = async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { sendResetLink, isLoading, error, success };
}

export function useResetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = async (token: string, newPassword: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { reset, isLoading, error, success };
}

export function useVerifyEmail() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const verify = async (email: string, otpCode: string, userId?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await verifyEmail(email, otpCode, userId);
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { verify, isLoading, error, success };
}

export function useVerifyPhone() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const verify = async (phone: string, otpCode: string, userId?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await verifyPhone(phone, otpCode, userId);
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { verify, isLoading, error, success };
}

export function useResendVerification() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const resend = async (type: 'email' | 'phone', email?: string, phone?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await resendVerification(type, email, phone);
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { resend, isLoading, error, success };
}

export function useLogoutAll() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await logoutAll();
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { logout, isLoading, error, success };
}

export function useEnable2FA() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const enable = async (): Promise<{ qrCode: string; secret: string }> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await enable2FA();
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { enable, isLoading, error };
}

export function useDisable2FA() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const disable = async (password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await disable2FA(password);
      setSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { disable, isLoading, error, success };
}

export function useVerify2FA() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const verify = async (code: string): Promise<{ verified: boolean }> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await verify2FA(code);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { verify, isLoading, error };
}

export function useBackupCodes() {
  const [data, setData] = useState<{ codes: string[] } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = async (): Promise<{ codes: string[] }> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getBackupCodes();
      setData(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { data, fetch, isLoading, error };
}
